import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { GetStorageSummaryCall } from "../../redux/api/report.api";
import { STORAGE_CHANGED_EVENT } from "../../utils/storageEvents";

const BLUE = "#0061FE";
const GREEN = "#22C55E";
const TEXT = "#0F172A";
const MUTED = "#64748B";

// Light values are the original look; dark values sit on the navy surface.
const PALETTE = {
  light: {
    text: TEXT,
    muted: MUTED,
    blue: BLUE,
    green: GREEN,
    cardBg: null,
    border: "1px solid rgba(148, 163, 184, 0.2)",
    shadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
    divider: "rgba(148, 163, 184, 0.28)",
    storageBadge: "linear-gradient(180deg, #E8F1FF 0%, #F5F9FF 100%)",
    licenseBadge: "linear-gradient(180deg, #E9F9EF 0%, #F4FDF7 100%)",
    storageTrack: "#E8F1FF",
    licenseTrack: "#E8F0E9",
  },
  dark: {
    text: "#E6EBF5",
    muted: "#8E9AB3",
    blue: "#4C8DFF",
    green: "#34D399",
    cardBg:
      "linear-gradient(120deg, rgba(76, 141, 255, 0.10) 0%, rgba(19, 26, 44, 0) 45%, rgba(52, 211, 153, 0.06) 100%), #131A2C",
    border: "1px solid rgba(148, 163, 184, 0.14)",
    shadow: "0 10px 30px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    divider: "rgba(148, 163, 184, 0.16)",
    storageBadge: "linear-gradient(180deg, rgba(76, 141, 255, 0.22) 0%, rgba(76, 141, 255, 0.10) 100%)",
    licenseBadge: "linear-gradient(180deg, rgba(52, 211, 153, 0.20) 0%, rgba(52, 211, 153, 0.08) 100%)",
    storageTrack: "rgba(76, 141, 255, 0.16)",
    licenseTrack: "rgba(52, 211, 153, 0.14)",
  },
};

function bytesToGbNumber(bytes = 0) {
  return Number(bytes || 0) / (1024 * 1024 * 1024);
}

function formatGb(gb) {
  if (!Number.isFinite(gb)) return "0";
  if (gb >= 10) return gb.toFixed(1);
  if (gb >= 1) return gb.toFixed(1);
  return gb.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function formatLicenseEnd(licenseEnd) {
  if (!licenseEnd) return null;
  const d = new Date(licenseEnd);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getLicenseProgress(licenseStart, licenseEnd) {
  const end = new Date(licenseEnd || "");
  if (Number.isNaN(end.getTime())) return { percent: 0, color: GREEN, daysLeft: null };
  const start = new Date(licenseStart || "");
  const now = new Date();
  const msDay = 24 * 60 * 60 * 1000;
  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msDay);

  let remainingPercent = 0;
  if (!Number.isNaN(start.getTime()) && end.getTime() > start.getTime()) {
    const totalMs = end.getTime() - start.getTime();
    const remainingMs = Math.max(0, end.getTime() - now.getTime());
    remainingPercent = (remainingMs / totalMs) * 100;
  } else {
    // Fallback: treat 365-day window ending at license end
    remainingPercent = Math.min(100, Math.max(0, (Math.max(0, daysLeft) / 365) * 100));
  }

  const percent = Math.min(100, Math.max(0, remainingPercent));
  const color = daysLeft <= 14 ? "#EF4444" : daysLeft <= 45 ? "#F59E0B" : GREEN;
  return { percent, color, daysLeft };
}

function canShowForRole(role) {
  const roleKey = String(role || "").toLowerCase();
  return roleKey === "prof" || roleKey === "professor" || roleKey === "university_admin";
}

function IconBadge({ children, bg, color }) {
  return (
    <MDBox
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2.2,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: bg,
        color,
      }}
    >
      {children}
    </MDBox>
  );
}

IconBadge.propTypes = {
  children: PropTypes.node,
  bg: PropTypes.string,
  color: PropTypes.string,
};

/**
 * Sticky top strip with Storage + License status for professors / uni admins.
 */
export default function StorageLicenseBar({ sticky = true, variant = "full" }) {
  const { t } = useTranslation();
  const [{ darkMode }] = useMaterialUIController();
  const c = darkMode ? PALETTE.dark : PALETTE.light;
  const role = useSelector((state) => state.auth?.role);
  const visible = canShowForRole(role);
  const [storage, setStorage] = useState({
    usedBytes: 0,
    limitBytes: 0,
    percent: 0,
    licenseStart: null,
    licenseEnd: null,
  });

  useEffect(() => {
    if (!visible) return undefined;
    let mounted = true;
    const load = () => {
      GetStorageSummaryCall().then((res) => {
        if (!mounted || !res) return;
        setStorage({
          usedBytes: Number(res.usedBytes || 0),
          limitBytes: Number(res.limitBytes || 0),
          percent: Number(res.percent || 0),
          licenseStart: res.licenseStart || null,
          licenseEnd: res.licenseEnd || null,
        });
      });
    };
    load();
    // Re-fetch after deletes/uploads, when the tab regains focus, and every
    // minute while visible (uploads finish in a background job).
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener(STORAGE_CHANGED_EVENT, load);
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60 * 1000);
    return () => {
      mounted = false;
      window.removeEventListener(STORAGE_CHANGED_EVENT, load);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [visible]);

  const licenseProgress = useMemo(
    () => getLicenseProgress(storage.licenseStart, storage.licenseEnd),
    [storage.licenseStart, storage.licenseEnd]
  );
  const licenseEndLabel = formatLicenseEnd(storage.licenseEnd);

  const usedGb = bytesToGbNumber(storage.usedBytes);
  const limitGb = bytesToGbNumber(storage.limitBytes);
  const freeGb = Math.max(0, limitGb - usedGb);
  const usedPercent = Math.min(100, Math.max(0, storage.percent || (limitGb > 0 ? (usedGb / limitGb) * 100 : 0)));

  if (!visible) return null;

  // Compact two-row card for the dark mobile navigation sheet.
  if (variant === "compact") {
    const rows = [
      {
        key: "storage",
        icon: <CloudOutlinedIcon sx={{ fontSize: 16 }} />,
        label: t("nav.storage"),
        value: `${formatGb(usedGb)} / ${formatGb(limitGb)} GB`,
        percent: usedPercent,
        color: usedPercent >= 90 ? "#EF4444" : "#60A5FA",
      },
      ...(licenseEndLabel
        ? [
            {
              key: "license",
              icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 16 }} />,
              label: t("nav.license", { defaultValue: "License" }),
              value:
                licenseProgress.daysLeft != null
                  ? t("nav.daysLeft", { count: Math.max(0, licenseProgress.daysLeft) })
                  : licenseEndLabel,
              percent: licenseProgress.percent,
              color: licenseProgress.color,
            },
          ]
        : []),
    ];
    return (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1.25,
          borderRadius: 2.5,
          backgroundColor: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {rows.map((row) => (
          <MDBox key={row.key}>
            <MDBox display="flex" alignItems="center" gap={0.75} mb={0.5}>
              <MDBox sx={{ display: "flex", color: row.color }}>{row.icon}</MDBox>
              <MDTypography sx={{ fontSize: 12, fontWeight: 700, color: "#fff", flex: 1 }}>
                {row.label}
              </MDTypography>
              <MDTypography
                sx={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.7)", whiteSpace: "nowrap" }}
              >
                {row.value}
              </MDTypography>
            </MDBox>
            <LinearProgress
              variant="determinate"
              value={row.percent}
              sx={{
                height: 5,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.12)",
                "& .MuiLinearProgress-bar": { borderRadius: 999, backgroundColor: row.color },
              }}
            />
          </MDBox>
        ))}
      </MDBox>
    );
  }

  return (
    <MDBox
      sx={{
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: 1100,
        flexShrink: 0,
        // Keep left/right edges aligned with DashboardLayout gutter (no negative margins).
        // mb: 3 matches DashboardLayout horizontal padding (px: 3) so gap below = side gutters.
        width: "100%",
        mx: 0,
        mb: 3,
        px: 0,
        pt: 0,
        pb: 0,
        bgcolor: "transparent",
      }}
    >
      <MDBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch",
          gap: { xs: 1.25, md: 0 },
          bgcolor: "background.card",
          ...(c.cardBg ? { background: c.cardBg } : {}),
          borderRadius: 3,
          border: c.border,
          boxShadow: c.shadow,
          overflow: "hidden",
        }}
      >
        {/* Storage */}
        <MDBox
          sx={{
            flex: "1 1 280px",
            minWidth: 240,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <IconBadge bg={c.storageBadge} color={c.blue}>
            <CloudOutlinedIcon sx={{ fontSize: 22 }} />
          </IconBadge>
          <MDBox sx={{ flex: 1, minWidth: 0 }}>
            <MDBox display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mb={0.35}>
              <MDTypography sx={{ fontSize: 14, fontWeight: 800, color: c.text, lineHeight: 1.2 }}>
                {t("nav.storage")}
              </MDTypography>
              <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: c.text, whiteSpace: "nowrap" }}>
                {formatGb(freeGb)} GB {t("nav.free", { defaultValue: "free" })}
              </MDTypography>
            </MDBox>
            <MDTypography sx={{ fontSize: 12, color: c.muted, mb: 0.85, lineHeight: 1.2 }}>
              {formatGb(usedGb)} GB {t("nav.of", { defaultValue: "of" })} {formatGb(limitGb)} GB{" "}
              {t("nav.used", { defaultValue: "used" })}
            </MDTypography>
            <LinearProgress
              variant="determinate"
              value={usedPercent}
              sx={{
                height: 7,
                borderRadius: 999,
                bgcolor: c.storageTrack,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  backgroundColor: usedPercent >= 90 ? "#EF4444" : c.blue,
                },
              }}
            />
          </MDBox>
        </MDBox>

        {licenseEndLabel ? (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", md: "block" },
                borderColor: c.divider,
                my: 1.5,
              }}
            />
            {/* License */}
            <MDBox
              sx={{
                flex: "1 1 280px",
                minWidth: 240,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
              }}
            >
              <IconBadge bg={c.licenseBadge} color={c.green}>
                <WorkspacePremiumOutlinedIcon sx={{ fontSize: 22 }} />
              </IconBadge>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <MDBox display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mb={0.35}>
                  <MDTypography sx={{ fontSize: 14, fontWeight: 800, color: c.text, lineHeight: 1.2 }}>
                    {t("nav.license", { defaultValue: "License" })}
                  </MDTypography>
                  <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: c.text, whiteSpace: "nowrap" }}>
                    {licenseEndLabel}
                  </MDTypography>
                </MDBox>
                <MDTypography sx={{ fontSize: 12, color: c.muted, mb: 0.85, lineHeight: 1.2 }}>
                  {licenseProgress.daysLeft != null
                    ? t("nav.daysLeft", { count: Math.max(0, licenseProgress.daysLeft) })
                    : t("nav.licenseProgress")}
                </MDTypography>
                <LinearProgress
                  variant="determinate"
                  value={licenseProgress.percent}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    bgcolor: c.licenseTrack,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      backgroundColor: licenseProgress.color,
                    },
                  }}
                />
              </MDBox>
            </MDBox>
          </>
        ) : null}
      </MDBox>
    </MDBox>
  );
}

StorageLicenseBar.propTypes = {
  sticky: PropTypes.bool,
  variant: PropTypes.oneOf(["full", "compact"]),
};
